// System-metric bands — the client side of the Alert/Metrics unification.
// Screens NEVER hardcode thresholds anymore: they read a metric's server-computed
// band (Tier A, aggregate KPIs) or re-apply the server-delivered warn/critical +
// direction to a per-row value via `bandFor` (Tier B). The banding math lives
// here ONCE and mirrors the Rust `MetricDirection::band` (inclusive on the breach
// side; critical dominates warn) so a cell can never disagree with an alert.

import type { MetricDirection, StatusBand, SystemMetricStatus } from '@/api/types.gen';
import { useSystemHealth } from '@/api/hooks';

/** The three paintable bands (the server's `unknown` collapses to a muted tone). */
export type Band = 'ok' | 'warn' | 'critical' | 'unknown';

/** CSS color for a band. `unknown` (stale / no data) is muted — never green/red. */
export function bandColor(b: Band): string {
  switch (b) {
    case 'critical':
      return 'var(--bad)';
    case 'warn':
      return 'var(--warn)';
    case 'ok':
      return 'var(--good)';
    default:
      return 'var(--ink-3)';
  }
}

/** The thresholds needed to band a raw value — the subset a screen reads off a metric. */
export interface Bandable {
  warn: number;
  critical: number;
  direction: MetricDirection;
}

/**
 * Band a raw value against centrally-delivered thresholds (Tier B). `warn`/
 * `critical` are the *breach* thresholds — inclusive on the breach side, and
 * `critical` always wins. Returns `fallback` (default 'ok') when the metric isn't
 * catalogued yet, so a not-yet-wired cell degrades cleanly instead of throwing.
 */
export function bandFor(
  m: Bandable | undefined,
  value: number,
  fallback: Band = 'ok',
): Band {
  if (!m || !Number.isFinite(value)) return fallback;
  if (m.direction === 'lower_is_better') {
    if (value >= m.critical) return 'critical';
    if (value >= m.warn) return 'warn';
    return 'ok';
  }
  // higher_is_better
  if (value <= m.critical) return 'critical';
  if (value <= m.warn) return 'warn';
  return 'ok';
}

/** Convenience: band a value AND map it to a color in one call. */
export function bandColorFor(m: Bandable | undefined, value: number, fallback: Band = 'ok'): string {
  return bandColor(bandFor(m, value, fallback));
}

/**
 * The live status of one system metric by its stable catalog key
 * (`encore.identity_coverage_pct`, `events.avg_capacity_pct`, …). `undefined`
 * while loading, when the role can't see it, or when the key isn't catalogued.
 */
export function useSystemMetric(key: string, enabled: boolean = true): SystemMetricStatus | undefined {
  const q = useSystemHealth(enabled);
  return q.data?.metrics.find((m) => m.key === key);
}

/**
 * `.sync-pill` / `.sync-dot` CSS variant for a band (`critical` reuses the
 * `bad` class; `ok` has no dot rule so it stays green; `unknown` → muted).
 * The single source used by the shared `SyncDot`/`SyncPill` so no sync-dot is
 * ever left bare-green.
 */
export function pillClass(b: Band | 'error'): string {
  // `error` is the topbar chip's extra tier (between warn and critical); the
  // shared metric Band never carries it.
  return b === 'critical' ? 'bad' : b === 'error' ? 'err' : b;
}

/** Human label for a band (used on chips/pills). */
export function bandLabel(b: Band): string {
  switch (b) {
    case 'critical':
      return 'Critical';
    case 'warn':
      return 'Warning';
    case 'ok':
      return 'Healthy';
    default:
      return 'Stale';
  }
}
